'use client';

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import { mapPipelineRow, PIPELINE_COLUMNS } from '@/components/common/CustomerCardPanel';
import type { CustomerCard, StageKey } from '@/components/common/CustomerCardPanel';
import ContactProfile from './ContactProfile';
import { getChatCompletion } from '@/lib/ai/chatCompletion';
import { useAuth, type UserRole } from '@/contexts/AuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';

// Retry-on-429 / timeout wrapper for the /api/rbac/list-contacts fetch.
// Aborts (Strict Mode unmount) are not retried.
async function fetchListContactsWithRetry(
  payload: { effectiveRoles: UserRole[]; effectiveUserId: string | null },
  signal: AbortSignal,
  maxAttempts = 3,
  timeoutMs = 8000,
): Promise<Response> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error(`Request timed out (attempt ${attempt + 1}/${maxAttempts})`)),
        timeoutMs,
      ),
    );
    try {
      const res = await Promise.race([
        fetch('/api/rbac/list-contacts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal,
        }),
        timeoutPromise,
      ]);
      if (res.status === 429 && attempt < maxAttempts - 1) {
        console.warn(`[Contacts] 429 on attempt ${attempt + 1}/${maxAttempts}, backing off`);
        await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt))); // 1s, 2s, 4s
        continue;
      }
      return res;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if ((err as { name?: string })?.name === 'AbortError' || signal.aborted) throw err;
      const isTimeout = msg.includes('timed out');
      console.warn(`[Contacts] attempt ${attempt + 1} failed: ${msg}`);
      if (attempt === maxAttempts - 1 || !isTimeout) throw err;
      await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt)));
    }
  }
  throw new Error('[Contacts] exhausted retry attempts');
}

// ─── AI Segment Analysis Panel ──────────────────────────────────────────────
interface SegmentSuggestion {
  name: string;
  description: string;
  criteria: string;
  estimatedSize: number;
  propensityScore: number;
  avgContractValue: number;
  primaryStages: string[];
  campaignAngle: string;
  priority: 'high' | 'medium' | 'low';
}

interface SegmentAnalysisPanelProps {
  contacts: CustomerCard[];
  contactTagMap: Record<string, ContactTag[]>;
  onApplySegment: (segment: SegmentSuggestion) => void;
  onClose: () => void;
}

function SegmentAnalysisPanel({ contacts, contactTagMap, onApplySegment, onClose }: SegmentAnalysisPanelProps) {
  const [loading, setLoading] = useState(false);
  const [segments, setSegments] = useState<SegmentSuggestion[]>([]);
  const [error, setError] = useState('');
  const [analyzed, setAnalyzed] = useState(false);

  const runAnalysis = async () => {
    setLoading(true);
    setError('');
    try {
      // Build a compact data summary for Gemini
      const stageDistribution: Record<string, number> = {};
      const locationDistribution: Record<string, number> = {};
      let totalValue = 0;
      let contactsWithEmail = 0;
      const contractBuckets = { under10k: 0, k10_25: 0, k25_50: 0, k50_100: 0, over100k: 0 };
      const activityBuckets = { today: 0, week: 0, month: 0, quarter: 0, older: 0 };
      const now = Date.now();

      for (const c of contacts) {
        stageDistribution[c.stage] = (stageDistribution[c.stage] ?? 0) + 1;
        const loc = c.state || c.city || 'Unknown';
        locationDistribution[loc] = (locationDistribution[loc] ?? 0) + 1;
        totalValue += c.contractValue ?? 0;
        if (c.email) contactsWithEmail++;
        const v = c.contractValue ?? 0;
        if (v < 10000) contractBuckets.under10k++;
        else if (v < 25000) contractBuckets.k10_25++;
        else if (v < 50000) contractBuckets.k25_50++;
        else if (v < 100000) contractBuckets.k50_100++;
        else contractBuckets.over100k++;
        if (c.columnEnteredAt) {
          const days = Math.floor((now - new Date(c.columnEnteredAt).getTime()) / 86400000);
          if (days <= 1) activityBuckets.today++;
          else if (days <= 7) activityBuckets.week++;
          else if (days <= 30) activityBuckets.month++;
          else if (days <= 90) activityBuckets.quarter++;
          else activityBuckets.older++;
        }
      }

      const topLocations = Object.entries(locationDistribution)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8)
        .map(([loc, count]) => `${loc}: ${count}`)
        .join(', ');

      const stageBreakdown = Object.entries(stageDistribution)
        .map(([s, n]) => `${s}: ${n}`)
        .join(', ');

      const avgValue = contacts.length > 0 ? Math.round(totalValue / contacts.length) : 0;

      const prompt = `You are a CRM analyst for a roofing company. Analyze this contact database and suggest 4 high-value customer segments for targeted campaigns.

CONTACT DATABASE SUMMARY:
- Total contacts: ${contacts.length}
- Contacts with email: ${contactsWithEmail}
- Average contract value: $${avgValue.toLocaleString()}
- Total pipeline value: $${totalValue.toLocaleString()}

STAGE DISTRIBUTION: ${stageBreakdown}

CONTRACT VALUE DISTRIBUTION:
- Under $10K: ${contractBuckets.under10k}
- $10K–$25K: ${contractBuckets.k10_25}
- $25K–$50K: ${contractBuckets.k25_50}
- $50K–$100K: ${contractBuckets.k50_100}
- Over $100K: ${contractBuckets.over100k}

ACTIVITY RECENCY:
- Active today: ${activityBuckets.today}
- Active this week: ${activityBuckets.week}
- Active this month: ${activityBuckets.month}
- Active this quarter: ${activityBuckets.quarter}
- Older than 90 days: ${activityBuckets.older}

TOP LOCATIONS: ${topLocations}

Return ONLY a JSON array of exactly 4 segment objects. Each object must have:
{
  "name": "Segment name (short, 3-5 words)",
  "description": "1-2 sentence description of who these contacts are",
  "criteria": "Specific filter criteria to identify them (e.g., stage + value range)",
  "estimatedSize": <estimated number of contacts matching>,
  "propensityScore": <0-100 integer, likelihood to convert/purchase>,
  "avgContractValue": <estimated average contract value in dollars>,
  "primaryStages": ["stage-id-1", "stage-id-2"],
  "campaignAngle": "Specific campaign message angle for this segment",
  "priority": "high" | "medium" | "low"
}

Focus on: high-value prospects, re-engagement opportunities, close-to-close leads, and geographic clusters. Order by propensityScore descending.`;

      const result = await getChatCompletion(
        'GEMINI',
        'gemini/gemini-2.5-flash',
        [{ role: 'user', content: prompt }],
        { max_tokens: 1500, temperature: 0.4 }
      );

      const content = result?.choices?.[0]?.message?.content ?? '';
      const jsonMatch = content.match(/\[[\s\S]*\]/);
      if (!jsonMatch) throw new Error('Could not parse AI response');
      const parsed = JSON.parse(jsonMatch[0]) as SegmentSuggestion[];
      setSegments(parsed);
      setAnalyzed(true);
    } catch (e: any) {
      setError(e.message ?? 'Analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const priorityConfig = {
    high: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500', label: 'High Priority' },
    medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500', label: 'Medium Priority' },
    low: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200', dot: 'bg-gray-400', label: 'Low Priority' },
  };

  const scoreColor = (score: number) => {
    if (score >= 75) return 'text-green-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-500';
  };

  const scoreBarColor = (score: number) => {
    if (score >= 75) return 'bg-green-500';
    if (score >= 50) return 'bg-amber-500';
    return 'bg-red-400';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-3xl mx-0 sm:mx-4 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-100" style={{ background: 'linear-gradient(135deg, #000150 0%, #033494 100%)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
              <span className="text-lg">🧠</span>
            </div>
            <div>
              <h2 className="font-heading text-base font-bold text-white">AI Segment Intelligence</h2>
              <p className="font-caption text-xs text-blue-200">Gemini-powered propensity scoring · {contacts.length} contacts analyzed</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {!analyzed && !loading && (
            <div className="text-center py-10">
              <div className="w-20 h-20 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #DBEAFE 100%)' }}>
                <span className="text-4xl">🎯</span>
              </div>
              <h3 className="font-heading text-lg font-bold text-gray-900 mb-2">Discover High-Value Segments</h3>
              <p className="font-body text-sm text-gray-500 max-w-md mx-auto mb-6">
                Gemini will analyze your {contacts.length} contacts — contract values, pipeline stages, locations, and activity patterns — to identify the best segments for targeted campaigns with propensity scores.
              </p>
              <div className="flex items-center justify-center gap-6 mb-8 text-xs text-gray-400">
                <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-400" />Pipeline stage analysis</div>
                <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-400" />Contract value clustering</div>
                <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-purple-400" />Geographic patterns</div>
                <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" />Activity recency scoring</div>
              </div>
              <button
                onClick={runAnalysis}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#033494' }}
              >
                <span>✨</span>
                Analyze & Generate Segments
              </button>
            </div>
          )}

          {loading && (
            <div className="text-center py-16">
              <div className="relative w-16 h-16 mx-auto mb-5">
                <div className="w-16 h-16 border-4 border-blue-100 rounded-full" />
                <div className="absolute inset-0 w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <div className="absolute inset-0 flex items-center justify-center text-xl">🧠</div>
              </div>
              <p className="font-heading text-base font-semibold text-gray-800 mb-1">Analyzing Contact Patterns…</p>
              <p className="font-body text-sm text-gray-400">Gemini is scoring propensity and clustering segments</p>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm mb-4">
              <Icon name="ExclamationCircleIcon" size={18} />
              <span>{error}</span>
              <button onClick={runAnalysis} className="ml-auto text-xs font-semibold underline hover:no-underline">Retry</button>
            </div>
          )}

          {analyzed && segments.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <p className="font-body text-sm text-gray-500">{segments.length} segments identified — click <strong>Apply Segment</strong> to filter contacts</p>
                <button onClick={runAnalysis} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
                  <Icon name="ArrowPathIcon" size={13} />
                  Re-analyze
                </button>
              </div>

              {segments.map((seg, idx) => {
                const pc = priorityConfig[seg.priority] ?? priorityConfig.low;
                return (
                  <div key={idx} className={`rounded-xl border p-4 ${pc.bg} ${pc.border}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-white/70 flex items-center justify-center flex-shrink-0 text-base font-bold text-gray-600 shadow-sm">
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <h4 className="font-heading text-sm font-bold text-gray-900">{seg.name}</h4>
                            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/70 ${pc.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${pc.dot}`} />
                              {pc.label}
                            </span>
                          </div>
                          <p className="font-body text-xs text-gray-600 leading-relaxed">{seg.description}</p>
                        </div>
                      </div>

                      {/* Propensity Score */}
                      <div className="flex-shrink-0 text-center bg-white/70 rounded-xl px-3 py-2 shadow-sm min-w-[72px]">
                        <div className={`font-heading text-2xl font-black ${scoreColor(seg.propensityScore)}`}>{seg.propensityScore}</div>
                        <div className="font-caption text-[9px] text-gray-400 uppercase tracking-wider leading-tight">Propensity</div>
                        <div className="mt-1.5 w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                          <div className={`h-1.5 rounded-full transition-all ${scoreBarColor(seg.propensityScore)}`} style={{ width: `${seg.propensityScore}%` }} />
                        </div>
                      </div>
                    </div>

                    {/* Metrics row */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                      <div className="bg-white/60 rounded-lg px-3 py-2 text-center">
                        <div className="font-heading text-sm font-bold text-gray-800">~{seg.estimatedSize}</div>
                        <div className="font-caption text-[10px] text-gray-400">Est. Contacts</div>
                      </div>
                      <div className="bg-white/60 rounded-lg px-3 py-2 text-center">
                        <div className="font-heading text-sm font-bold text-gray-800">{formatCurrency(seg.avgContractValue)}</div>
                        <div className="font-caption text-[10px] text-gray-400">Avg. Value</div>
                      </div>
                      <div className="bg-white/60 rounded-lg px-3 py-2 text-center">
                        <div className="font-heading text-sm font-bold text-gray-800">{seg.primaryStages.length}</div>
                        <div className="font-caption text-[10px] text-gray-400">Key Stages</div>
                      </div>
                    </div>

                    {/* Criteria & Campaign angle */}
                    <div className="space-y-2 mb-3">
                      <div className="flex items-start gap-2">
                        <Icon name="FunnelIcon" size={12} className="text-gray-400 mt-0.5 flex-shrink-0" />
                        <p className="font-caption text-xs text-gray-600"><span className="font-semibold">Criteria:</span> {seg.criteria}</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <Icon name="MegaphoneIcon" size={12} className="text-gray-400 mt-0.5 flex-shrink-0" />
                        <p className="font-caption text-xs text-gray-600"><span className="font-semibold">Campaign angle:</span> {seg.campaignAngle}</p>
                      </div>
                    </div>

                    {/* Stage chips */}
                    {seg.primaryStages.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap mb-3">
                        {seg.primaryStages.map(s => {
                          const meta = getStageMeta(s as StageKey);
                          return (
                            <span key={s} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: `${meta.color}20`, color: meta.color }}>
                              <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
                              {meta.title}
                            </span>
                          );
                        })}
                      </div>
                    )}

                    <button
                      onClick={() => onApplySegment(seg)}
                      className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold text-white transition-opacity hover:opacity-90"
                      style={{ backgroundColor: '#033494' }}
                    >
                      <Icon name="FunnelIcon" size={13} />
                      Apply Segment Filter
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatCurrency(val: number): string {
  if (!val) return 'TBD';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
}

function getStageMeta(stage: StageKey) {
  return PIPELINE_COLUMNS.find(c => c.id === stage) ?? { title: stage, color: '#6B7280' };
}

function getDaysSince(dateStr: string | null | undefined, nowMs: number): number | null {
  if (!dateStr || !nowMs) return null;
  const diff = nowMs - new Date(dateStr).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

interface ParsedFilters {
  stage?: StageKey | 'all';
  contractMin?: number;
  contractMax?: number;
  location?: string;
  lastActivityDays?: number;
  nameSearch?: string;
}

interface ContactTag {
  id: string;
  name: string;
  color: string;
}

interface SavedFilter {
  id: string;
  name: string;
  filter_config: ParsedFilters & { contractRangeIdx?: number; lastActivityIdx?: number };
}

const CONTRACT_VALUE_OPTIONS = [
  { label: 'Any Value', min: 0, max: Infinity },
  { label: 'Under $10K', min: 0, max: 10000 },
  { label: '$10K – $25K', min: 10000, max: 25000 },
  { label: '$25K – $50K', min: 25000, max: 50000 },
  { label: '$50K – $100K', min: 50000, max: 100000 },
  { label: 'Over $100K', min: 100000, max: Infinity },
];

const LAST_ACTIVITY_OPTIONS = [
  { label: 'Any Time', days: Infinity },
  { label: 'Today', days: 1 },
  { label: 'Last 7 Days', days: 7 },
  { label: 'Last 30 Days', days: 30 },
  { label: 'Last 90 Days', days: 90 },
  { label: 'Over 90 Days', days: -90 },
];

const TAG_COLORS = [
  '#EF4444', '#F97316', '#F59E0B', '#10B981',
  '#3B82F6', '#8B5CF6', '#EC4899', '#6B7280',
];


// ─── Bulk Tag Modal ─────────────────────────────────────────────────────────
interface BulkTagModalProps {
  selectedContacts: CustomerCard[];
  availableTags: ContactTag[];
  onClose: () => void;
  onTagsApplied: () => void;
}

function BulkTagModal({ selectedContacts, availableTags, onClose, onTagsApplied }: BulkTagModalProps) {
  const supabase = createClient();
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());
  const [action, setAction] = useState<'add' | 'remove'>('add');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0]);
  const [showNewTag, setShowNewTag] = useState(false);

  const toggleTag = (id: string) => {
    setSelectedTagIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    const { data, error: err } = await supabase
      .from('contact_tags')
      .insert({ name: newTagName.trim(), color: newTagColor })
      .select()
      .single();
    if (!err && data) {
      setSelectedTagIds(prev => new Set([...prev, data.id]));
      setNewTagName('');
      setShowNewTag(false);
      onTagsApplied(); // refresh tags list
    }
  };

  const handleApply = async () => {
    if (selectedTagIds.size === 0) { setError('Select at least one tag'); return; }
    setLoading(true);
    setError('');
    try {
      const tagIdArr = Array.from(selectedTagIds);
      for (const contact of selectedContacts) {
        for (const tagId of tagIdArr) {
          if (action === 'add') {
            await supabase.from('contact_tag_assignments').upsert(
              { contact_id: contact.id, tag_id: tagId },
              { onConflict: 'contact_id,tag_id' }
            );
          } else {
            await supabase.from('contact_tag_assignments')
              .delete()
              .eq('contact_id', contact.id)
              .eq('tag_id', tagId);
          }
        }
      }
      onTagsApplied();
      onClose();
    } catch (e: any) {
      setError(e.message ?? 'Failed to apply tags');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Icon name="TagIcon" size={18} className="text-blue-600" />
            <h2 className="font-heading text-base font-bold text-gray-900">Bulk Tag</h2>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{selectedContacts.length} contacts</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><Icon name="XMarkIcon" size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Action toggle */}
          <div className="flex gap-2">
            <button onClick={() => setAction('add')} className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${action === 'add' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              Add Tags
            </button>
            <button onClick={() => setAction('remove')} className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${action === 'remove' ? 'border-red-400 bg-red-50 text-red-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              Remove Tags
            </button>
          </div>

          {/* Tag list */}
          <div>
            <label className="font-caption text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-2">Select Tags</label>
            <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
              {availableTags.map(tag => (
                <button
                  key={tag.id}
                  onClick={() => toggleTag(tag.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    selectedTagIds.has(tag.id) ? 'ring-2 ring-offset-1' : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{
                    background: `${tag.color}18`,
                    color: tag.color,
                    borderColor: selectedTagIds.has(tag.id) ? tag.color : `${tag.color}40`,
                    ringColor: tag.color,
                  }}
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: tag.color }} />
                  {tag.name}
                  {selectedTagIds.has(tag.id) && <Icon name="CheckIcon" size={11} />}
                </button>
              ))}
            </div>
          </div>

          {/* Create new tag */}
          {showNewTag ? (
            <div className="space-y-2 p-3 rounded-xl bg-gray-50 border border-gray-100">
              <input
                type="text"
                value={newTagName}
                onChange={e => setNewTagName(e.target.value)}
                placeholder="Tag name…"
                className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                onKeyDown={e => e.key === 'Enter' && handleCreateTag()}
              />
              <div className="flex items-center gap-2">
                <div className="flex gap-1.5 flex-wrap">
                  {TAG_COLORS.map(c => (
                    <button
                      key={c}
                      onClick={() => setNewTagColor(c)}
                      className={`w-5 h-5 rounded-full border-2 transition-transform ${newTagColor === c ? 'scale-125 border-gray-800' : 'border-transparent'}`}
                      style={{ background: c }}
                    />
                  ))}
                </div>
                <div className="flex gap-1 ml-auto">
                  <button onClick={() => setShowNewTag(false)} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                  <button onClick={handleCreateTag} className="px-3 py-1 text-xs font-medium text-white rounded-lg" style={{ backgroundColor: '#033494' }}>Create</button>
                </div>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowNewTag(true)} className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium">
              <Icon name="PlusIcon" size={13} />
              Create new tag
            </button>
          )}

          {error && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-100 text-red-600 text-sm">
              <Icon name="ExclamationCircleIcon" size={14} />
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 bg-gray-50">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-white">Cancel</button>
          <button
            onClick={handleApply}
            disabled={loading || selectedTagIds.size === 0}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: action === 'remove' ? '#EF4444' : '#033494' }}
          >
            {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="TagIcon" size={14} />}
            {loading ? 'Applying…' : `${action === 'add' ? 'Add' : 'Remove'} Tags`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Save Filter Modal ──────────────────────────────────────────────────────
interface SaveFilterModalProps {
  currentFilters: {
    stageFilter: StageKey | 'all';
    contractRange: number;
    locationFilter: string;
    lastActivityIdx: number;
  };
  onSave: (name: string) => void;
  onClose: () => void;
}

function SaveFilterModal({ currentFilters, onSave, onClose }: SaveFilterModalProps) {
  const [name, setName] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
        <h2 className="font-heading text-base font-bold text-gray-900 mb-1">Save Filter</h2>
        <p className="font-body text-sm text-gray-500 mb-4">Give this filter combination a name to reuse it later.</p>
        <div className="space-y-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-3 mb-4">
          {currentFilters.stageFilter !== 'all' && <div>Stage: <strong>{getStageMeta(currentFilters.stageFilter as StageKey).title}</strong></div>}
          {currentFilters.contractRange !== 0 && <div>Contract: <strong>{CONTRACT_VALUE_OPTIONS[currentFilters.contractRange].label}</strong></div>}
          {currentFilters.locationFilter && <div>Location: <strong>{currentFilters.locationFilter}</strong></div>}
          {currentFilters.lastActivityIdx !== 0 && <div>Activity: <strong>{LAST_ACTIVITY_OPTIONS[currentFilters.lastActivityIdx].label}</strong></div>}
        </div>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Filter name…"
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 mb-4"
          onKeyDown={e => e.key === 'Enter' && name.trim() && onSave(name.trim())}
          autoFocus
        />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
          <button
            onClick={() => name.trim() && onSave(name.trim())}
            disabled={!name.trim()}
            className="px-5 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: '#033494' }}
          >
            Save Filter
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────
export default function ContactsInteractive() {
  const supabase = createClient();
  const { currentUser } = useAuth();
  const { getEffectiveRoles, getEffectiveUserId } = useImpersonation();

  const effectiveRoles = getEffectiveRoles(currentUser.roles);
  const effectiveUserId = getEffectiveUserId(currentUser.id || null);

  const [contacts, setContacts] = useState<CustomerCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContact, setSelectedContact] = useState<CustomerCard | null>(null);
  const [now, setNow] = useState<number>(0);

  // AI search
  const [aiQuery, setAiQuery] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiParsed, setAiParsed] = useState<ParsedFilters | null>(null);
  const [aiSuggestion, setAiSuggestion] = useState<string>('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Smart filters
  const [stageFilter, setStageFilter] = useState<StageKey | 'all'>('all');
  const [contractRange, setContractRange] = useState(0);
  const [locationFilter, setLocationFilter] = useState('');
  const [lastActivityIdx, setLastActivityIdx] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Tags
  const [availableTags, setAvailableTags] = useState<ContactTag[]>([]);
  const [contactTagMap, setContactTagMap] = useState<Record<string, ContactTag[]>>({});
  const [tagFilter, setTagFilter] = useState<string | null>(null);

  // Saved filters
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [showSavedFilters, setShowSavedFilters] = useState(false);
  const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkTagModal, setShowBulkTagModal] = useState(false);
  const [showSegmentPanel, setShowSegmentPanel] = useState(false);

  // CSV
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState<{ inserted: number; errors: number } | null>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);

  // RBAC-3 — abort in-flight list-contacts fetch (Strict Mode safety) +
  // debounce Realtime refetches so a burst of row changes doesn't fan out.
  const abortRef = useRef<AbortController | null>(null);
  const realtimeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // RBAC-3 — load contacts via /api/rbac/list-contacts (service-role server-side filter).
  // The route applies resolveFilter(effectiveRoles, effectiveUserId, 'contacts') and
  // returns only the rows the effective identity should see. Never query
  // customer_pipeline directly from the browser for this list — RLS is open in dev,
  // so direct queries would leak rows across roles.
  const loadContacts = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    console.log('[Contacts] fetching with effectiveRoles=', effectiveRoles, 'effectiveUserId=', effectiveUserId);

    try {
      const res = await fetchListContactsWithRetry(
        { effectiveRoles, effectiveUserId },
        controller.signal,
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const json: { contacts?: unknown[]; filterMode?: string } = await res.json();
      setContacts((json.contacts ?? []).map(mapPipelineRow));
      console.log('[Contacts] received', json.contacts?.length, 'rows, filterMode=', json.filterMode);
    } catch (e: unknown) {
      if ((e as { name?: string })?.name === 'AbortError') return;
      const msg = (e as { message?: string })?.message ?? 'Failed to load contacts';
      console.error('[Contacts] error', msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [effectiveRoles, effectiveUserId]);

  const loadTags = useCallback(async () => {
    try {
      const { data: tags } = await supabase.from('contact_tags').select('*').order('name');
      const { data: assignments } = await supabase.from('contact_tag_assignments').select('contact_id, tag_id, contact_tags(id, name, color)');
      if (tags) setAvailableTags(tags);
      if (assignments) {
        const map: Record<string, ContactTag[]> = {};
        for (const a of assignments) {
          const tag = (a as any).contact_tags as ContactTag;
          if (!tag) continue;
          if (!map[a.contact_id]) map[a.contact_id] = [];
          map[a.contact_id].push(tag);
        }
        setContactTagMap(map);
      }
    } catch { /* tags are optional */ }
  }, [supabase]);

  const loadSavedFilters = useCallback(async () => {
    try {
      const { data } = await supabase.from('contact_saved_filters').select('*').order('name');
      if (data) setSavedFilters(data as SavedFilter[]);
    } catch { /* saved filters are optional */ }
  }, [supabase]);

  // Bootstrap: load contacts → 150ms gap → tags → 150ms gap → saved filters.
  // Sequential with gaps to avoid 429s on cold connections (CLAUDE.md).
  // Re-runs when impersonation changes effective identity.
  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      await loadContacts();
      if (cancelled) return;
      await new Promise((r) => setTimeout(r, 150));
      if (cancelled) return;
      await loadTags();
      if (cancelled) return;
      await new Promise((r) => setTimeout(r, 150));
      if (cancelled) return;
      await loadSavedFilters();
    };
    bootstrap();
    setNow(Date.now());
    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
  }, [loadContacts, loadTags, loadSavedFilters]);

  // Realtime: debounce 500ms then refetch via API route (so the assigned_pm_id
  // filter is re-applied — direct state patching would leak other users' rows).
  useEffect(() => {
    const channel = supabase
      .channel('contacts_page_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'customer_pipeline' }, () => {
        if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
        realtimeDebounceRef.current = setTimeout(() => { loadContacts(); }, 500);
      })
      .subscribe();
    return () => {
      if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
      supabase.removeChannel(channel);
    };
  }, [loadContacts, supabase]);

  const applySavedFilter = (filter: SavedFilter) => {
    const cfg = filter.filter_config;
    if (cfg.stage) setStageFilter(cfg.stage as StageKey | 'all');
    if (cfg.contractRangeIdx !== undefined) setContractRange(cfg.contractRangeIdx);
    if (cfg.location) setLocationFilter(cfg.location);
    if (cfg.lastActivityIdx !== undefined) setLastActivityIdx(cfg.lastActivityIdx);
    setShowSavedFilters(false);
    setShowFilters(true);
  };

  const handleSaveFilter = async (name: string) => {
    const config = {
      stage: stageFilter !== 'all' ? stageFilter : undefined,
      contractRangeIdx: contractRange !== 0 ? contractRange : undefined,
      location: locationFilter || undefined,
      lastActivityIdx: lastActivityIdx !== 0 ? lastActivityIdx : undefined,
    };
    await supabase.from('contact_saved_filters').upsert({ name, filter_config: config }, { onConflict: 'name' });
    await loadSavedFilters();
    setShowSaveFilterModal(false);
  };

  const handleDeleteSavedFilter = async (id: string) => {
    await supabase.from('contact_saved_filters').delete().eq('id', id);
    await loadSavedFilters();
  };

  // AI parse query with debounce
  const parseWithAI = useCallback(async (query: string) => {
    if (!query.trim()) { setAiParsed(null); setAiSuggestion(''); return; }
    setAiLoading(true);
    try {
      const stageList = PIPELINE_COLUMNS.map(c => `"${c.id}" (${c.title})`).join(', ');
      const result = await getChatCompletion(
        'GEMINI', 'gemini/gemini-2.5-flash',
        [
          {
            role: 'system',
            content: `You are a CRM search assistant. Parse user queries about contacts into structured JSON filters.
Available stages: ${stageList}.
Return ONLY valid JSON with these optional fields:
{
  "stage": "<stage-id or null>",
  "contractMin": <number or null>,
  "contractMax": <number or null>,
  "location": "<city or state string or null>",
  "lastActivityDays": <number: positive means within N days, negative means older than N days, null if not specified>,
  "nameSearch": "<name string or null>"
}`
          },
          { role: 'user', content: query }
        ],
        { max_completion_tokens: 200 }
      );
      const content = result?.choices?.[0]?.message?.content ?? '';
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]) as ParsedFilters;
        setAiParsed(parsed);
        const parts: string[] = [];
        if (parsed.stage) parts.push(`Stage: ${getStageMeta(parsed.stage as StageKey).title}`);
        if (parsed.contractMin && parsed.contractMax) parts.push(`Value: ${formatCurrency(parsed.contractMin)}–${formatCurrency(parsed.contractMax)}`);
        else if (parsed.contractMin) parts.push(`Value > ${formatCurrency(parsed.contractMin)}`);
        else if (parsed.contractMax) parts.push(`Value < ${formatCurrency(parsed.contractMax)}`);
        if (parsed.location) parts.push(`Location: ${parsed.location}`);
        if (parsed.lastActivityDays) parts.push(parsed.lastActivityDays > 0 ? `Active within ${parsed.lastActivityDays}d` : `Inactive ${Math.abs(parsed.lastActivityDays)}d+`);
        if (parsed.nameSearch) parts.push(`Name: ${parsed.nameSearch}`);
        setAiSuggestion(parts.length ? parts.join(' · ') : 'Showing all contacts');
      }
    } catch { setAiParsed(null); }
    finally { setAiLoading(false); }
  }, []);

  const handleAiQueryChange = (val: string) => {
    setAiQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val.trim()) { setAiParsed(null); setAiSuggestion(''); return; }
    debounceRef.current = setTimeout(() => parseWithAI(val), 700);
  };

  const clearAll = () => {
    setAiQuery(''); setAiParsed(null); setAiSuggestion('');
    setStageFilter('all'); setContractRange(0); setLocationFilter(''); setLastActivityIdx(0);
    setTagFilter(null); setSelectedIds(new Set());
  };

  const hasActiveFilters = aiQuery || stageFilter !== 'all' || contractRange !== 0 || locationFilter || lastActivityIdx !== 0 || tagFilter;

  const filtered = useMemo(() => {
    if (now === 0) return contacts;
    const contractOpt = CONTRACT_VALUE_OPTIONS[contractRange];
    const activityOpt = LAST_ACTIVITY_OPTIONS[lastActivityIdx];
    const locLower = locationFilter.toLowerCase().trim();

    return contacts.filter(c => {
      // Tag filter
      if (tagFilter) {
        const tags = contactTagMap[c.id] ?? [];
        if (!tags.some(t => t.id === tagFilter)) return false;
      }

      if (aiParsed) {
        if (aiParsed.stage && aiParsed.stage !== 'all' && c.stage !== aiParsed.stage) return false;
        if (aiParsed.contractMin && c.contractValue < aiParsed.contractMin) return false;
        if (aiParsed.contractMax && c.contractValue > aiParsed.contractMax) return false;
        if (aiParsed.location) {
          const loc = aiParsed.location.toLowerCase();
          if (!c.city.toLowerCase().includes(loc) && !c.state.toLowerCase().includes(loc) && !c.address.toLowerCase().includes(loc)) return false;
        }
        if (aiParsed.lastActivityDays) {
          const days = c.columnEnteredAt ? Math.floor((now - new Date(c.columnEnteredAt).getTime()) / (1000 * 60 * 60 * 24)) : null;
          if (days === null) return false;
          if (aiParsed.lastActivityDays > 0 && days > aiParsed.lastActivityDays) return false;
          if (aiParsed.lastActivityDays < 0 && days < Math.abs(aiParsed.lastActivityDays)) return false;
        }
        if (aiParsed.nameSearch) {
          const ns = aiParsed.nameSearch.toLowerCase();
          const fullName = `${c.customerFirstName} ${c.customerLastName}`.toLowerCase();
          if (!fullName.includes(ns) && !c.email.toLowerCase().includes(ns)) return false;
        }
        return true;
      }

      if (stageFilter !== 'all' && c.stage !== stageFilter) return false;
      if (contractOpt.max !== Infinity && c.contractValue > contractOpt.max) return false;
      if (contractOpt.min > 0 && c.contractValue < contractOpt.min) return false;
      if (locLower && !c.city.toLowerCase().includes(locLower) && !c.state.toLowerCase().includes(locLower)) return false;
      if (activityOpt.days !== Infinity) {
        const days = c.columnEnteredAt ? Math.floor((now - new Date(c.columnEnteredAt).getTime()) / (1000 * 60 * 60 * 24)) : null;
        if (days === null) return false;
        if (activityOpt.days > 0 && days > activityOpt.days) return false;
        if (activityOpt.days < 0 && days < Math.abs(activityOpt.days)) return false;
      }
      return true;
    });
  }, [contacts, aiParsed, stageFilter, contractRange, locationFilter, lastActivityIdx, now, tagFilter, contactTagMap]);

  // Bulk selection helpers
  const allFilteredSelected = filtered.length > 0 && filtered.every(c => selectedIds.has(c.id));
  const someSelected = selectedIds.size > 0;
  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(c => c.id)));
    }
  };
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectedContacts = useMemo(() => filtered.filter(c => selectedIds.has(c.id)), [filtered, selectedIds]);

  // CSV Export
  const handleExportCSV = useCallback(() => {
    const headers = ['First Name', 'Last Name', 'Email', 'Phone', 'Address', 'City', 'State', 'Zip', 'Stage', 'Contract Value', 'Total Squares', 'Material', 'Referral Source', 'Job ID'];
    const rows = filtered.map(c => [c.customerFirstName, c.customerLastName, c.email, c.phone, c.address, c.city, c.state, c.zip, c.stage, c.contractValue, c.totalSquares, c.material ?? '', c.referralSource ?? '', c.jobId]);
    const escape = (val: string | number) => {
      const s = String(val ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers, ...rows].map(row => row.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `contacts_export_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }, [filtered]);

  // CSV Import
  const handleCSVUpload = useCallback(async (file: File) => {
    if (!file) return;
    setCsvUploading(true); setCsvResult(null);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) throw new Error('CSV must have a header row and at least one data row.');
      const rawHeaders = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'));
      const parseRow = (line: string): string[] => {
        const result: string[] = []; let current = ''; let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') { if (inQuotes && line[i + 1] === '"') { current += '"'; i++; } else { inQuotes = !inQuotes; } }
          else if (ch === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
          else { current += ch; }
        }
        result.push(current.trim()); return result;
      };
      const col = (row: string[], ...names: string[]): string => {
        for (const name of names) { const idx = rawHeaders.findIndex(h => h.includes(name)); if (idx !== -1) return row[idx]?.trim() ?? ''; }
        return '';
      };
      const validStages = PIPELINE_COLUMNS.map(c => c.id);
      let inserted = 0; let errors = 0;
      for (let i = 1; i < lines.length; i++) {
        const row = parseRow(lines[i]);
        if (row.every(v => !v)) continue;
        const firstName = col(row, 'first_name', 'firstname', 'first');
        const lastName = col(row, 'last_name', 'lastname', 'last');
        const email = col(row, 'email');
        if (!firstName && !lastName && !email) { errors++; continue; }
        const stageRaw = col(row, 'stage').toLowerCase().replace(/\s+/g, '-');
        const stage = validStages.includes(stageRaw as StageKey) ? stageRaw : 'design';
        const { error: insertErr } = await supabase.from('customer_pipeline').insert({
          customer_first_name: firstName, customer_last_name: lastName || '(Unknown)',
          email: email || null, phone: col(row, 'phone') || null,
          address: col(row, 'address') || null, city: col(row, 'city') || null,
          state: col(row, 'state') || null, zip: col(row, 'zip') || null,
          stage, contract_value: parseFloat(col(row, 'contract_value', 'contract', 'value').replace(/[$,]/g, '')) || 0,
          total_squares: parseFloat(col(row, 'total_squares', 'squares')) || 0,
          material: col(row, 'material') || null, referral_source: col(row, 'referral_source', 'referral') || null,
          job_id: col(row, 'job_id', 'jobid', 'job') || `JOB-${Date.now()}-${i}`,
          column_entered_at: new Date().toISOString(),
        });
        if (insertErr) { errors++; } else { inserted++; }
      }
      setCsvResult({ inserted, errors });
      if (inserted > 0) await loadContacts();
    } catch { setCsvResult({ inserted: 0, errors: -1 }); }
    finally { setCsvUploading(false); if (csvInputRef.current) csvInputRef.current.value = ''; }
  }, [supabase]);

  const handleApplySegment = (segment: SegmentSuggestion) => {
    // Apply the first primary stage if available
    if (segment.primaryStages.length > 0) {
      setStageFilter(segment.primaryStages[0] as StageKey | 'all');
    }
    // Apply contract value range based on avgContractValue
    const avg = segment.avgContractValue;
    if (avg >= 100000) setContractRange(5);
    else if (avg >= 50000) setContractRange(4);
    else if (avg >= 25000) setContractRange(3);
    else if (avg >= 10000) setContractRange(2);
    else setContractRange(1);
    setShowFilters(true);
    setShowSegmentPanel(false);
  };

  if (selectedContact) {
    return (
      <ContactProfile
        contact={selectedContact}
        onBack={() => setSelectedContact(null)}
        allContacts={filtered}
        onSelectContact={setSelectedContact}
      />
    );
  }

  return (
    <div className="flex flex-col h-full min-h-screen bg-gray-50">
      {/* Modals */}
      {showBulkTagModal && (
        <BulkTagModal
          selectedContacts={selectedContacts}
          availableTags={availableTags}
          onClose={() => setShowBulkTagModal(false)}
          onTagsApplied={() => { loadTags(); setSelectedIds(new Set()); }}
        />
      )}
      {showSaveFilterModal && (
        <SaveFilterModal
          currentFilters={{ stageFilter, contractRange, locationFilter, lastActivityIdx }}
          onSave={handleSaveFilter}
          onClose={() => setShowSaveFilterModal(false)}
        />
      )}
      {showSegmentPanel && (
        <SegmentAnalysisPanel
          contacts={contacts}
          contactTagMap={contactTagMap}
          onApplySegment={handleApplySegment}
          onClose={() => setShowSegmentPanel(false)}
        />
      )}

      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-heading text-xl font-bold" style={{ color: '#000150' }}>Contacts</h1>
            <p className="font-caption text-xs text-gray-500 mt-0.5" suppressHydrationWarning>
              {loading ? 'Loading…' : `${filtered.length} of ${contacts.length} contacts`}
              {someSelected && <span className="ml-2 text-blue-600 font-semibold">· {selectedIds.size} selected</span>}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* AI Segment Intelligence button */}
            <button
              onClick={() => setShowSegmentPanel(true)}
              disabled={loading || contacts.length === 0}
              suppressHydrationWarning
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ borderColor: '#033494', color: '#033494', background: '#EEF2FF' }}
            >
              <span className="text-base leading-none">🧠</span>
              Segment AI
            </button>

            {/* Saved Filters */}
            <div className="relative">
              <button
                onClick={() => setShowSavedFilters(v => !v)}
                suppressHydrationWarning
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${showSavedFilters ? 'border-purple-400 text-purple-600 bg-purple-50' : 'border-gray-200 text-gray-600 bg-white hover:bg-gray-50'}`}
              >
                <Icon name="BookmarkIcon" size={15} />
                Saved
                {savedFilters.length > 0 && <span className="w-4 h-4 rounded-full bg-purple-100 text-purple-700 text-[10px] font-bold flex items-center justify-center">{savedFilters.length}</span>}
              </button>
              {showSavedFilters && (
                <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-xl shadow-xl border border-gray-100 z-30 overflow-hidden">
                  <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                    <span className="font-caption text-xs font-semibold text-gray-500 uppercase tracking-wider">Saved Filters</span>
                    {hasActiveFilters && !aiQuery && (
                      <button onClick={() => setShowSaveFilterModal(true)} className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                        <Icon name="PlusIcon" size={11} /> Save current
                      </button>
                    )}
                  </div>
                  {savedFilters.length === 0 ? (
                    <div className="px-3 py-4 text-center text-xs text-gray-400">No saved filters yet.<br />Apply filters and save them here.</div>
                  ) : (
                    <div className="max-h-60 overflow-y-auto">
                      {savedFilters.map(f => (
                        <div key={f.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 group">
                          <button onClick={() => applySavedFilter(f)} className="flex-1 text-left text-sm font-medium text-gray-700 hover:text-blue-600 truncate">
                            {f.name}
                          </button>
                          <button onClick={() => handleDeleteSavedFilter(f.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-400 transition-all ml-2">
                            <Icon name="TrashIcon" size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {hasActiveFilters && !aiQuery && savedFilters.length > 0 && (
                    <div className="px-3 py-2 border-t border-gray-100">
                      <button onClick={() => setShowSaveFilterModal(true)} className="w-full text-center text-xs text-blue-600 hover:text-blue-800 font-medium py-1">
                        + Save current filters
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={() => setShowFilters(v => !v)}
              suppressHydrationWarning
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${showFilters ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-gray-200 text-gray-600 bg-white hover:bg-gray-50'}`}
            >
              <Icon name="AdjustmentsHorizontalIcon" size={15} />
              Filters
              {hasActiveFilters && !aiQuery && <span className="w-2 h-2 rounded-full bg-blue-500 ml-0.5" />}
            </button>

            <button onClick={handleExportCSV} disabled={filtered.length === 0} suppressHydrationWarning className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <Icon name="ArrowDownTrayIcon" size={15} />
              Export CSV
            </button>

            <label className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 transition-colors cursor-pointer ${csvUploading ? 'opacity-60 pointer-events-none' : ''}`}>
              {csvUploading ? <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /> : <Icon name="ArrowUpTrayIcon" size={15} />}
              {csvUploading ? 'Importing…' : 'Import CSV'}
              <input ref={csvInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleCSVUpload(f); }} />
            </label>

            <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90" style={{ backgroundColor: '#033494' }}>
              <Icon name="PlusIcon" size={16} />
              Add Contact
            </button>
          </div>
        </div>

        {/* CSV Result Toast */}
        {csvResult && (
          <div className={`flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg mb-3 text-sm font-medium ${csvResult.errors === -1 ? 'bg-red-50 border border-red-200 text-red-700' : csvResult.inserted > 0 ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-yellow-50 border border-yellow-200 text-yellow-700'}`}>
            <div className="flex items-center gap-2">
              {csvResult.errors === -1 ? <><Icon name="ExclamationTriangleIcon" size={15} /> Failed to parse CSV.</> :
                <><Icon name="CheckCircleIcon" size={15} />
                  {csvResult.inserted > 0 && <span>{csvResult.inserted} contact{csvResult.inserted !== 1 ? 's' : ''} imported.</span>}
                  {csvResult.errors > 0 && <span className="ml-1 text-yellow-600">{csvResult.errors} row{csvResult.errors !== 1 ? 's' : ''} skipped.</span>}
                </>}
            </div>
            <button onClick={() => setCsvResult(null)}><Icon name="XMarkIcon" size={14} /></button>
          </div>
        )}

        {/* AI Search Bar */}
        <div className="relative mb-3">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none">
            {aiLoading ? <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /> : <span className="text-base">✨</span>}
          </div>
          <input
            type="text"
            value={aiQuery}
            onChange={e => handleAiQueryChange(e.target.value)}
            placeholder={'AI Search — try "contracts over $50K in Texas" or "inactive leads last 30 days"…'}
            className="w-full pl-9 pr-10 py-2.5 rounded-xl border-2 border-blue-100 font-body text-sm focus:outline-none focus:border-blue-400 bg-gradient-to-r from-blue-50/50 to-white transition-colors"
            suppressHydrationWarning
          />
          {aiQuery && (
            <button onClick={() => { setAiQuery(''); setAiParsed(null); setAiSuggestion(''); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <Icon name="XMarkIcon" size={15} />
            </button>
          )}
        </div>

        {aiSuggestion && (
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-50 border border-blue-100 text-blue-700">
              <span>🎯</span><span>{aiSuggestion}</span>
            </div>
            <span className="font-caption text-xs text-gray-400">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
          </div>
        )}

        {/* Smart Filters Panel */}
        {showFilters && !aiQuery && (
          <div className="flex flex-wrap items-end gap-3 pt-3 border-t border-gray-100">
            {/* Stage */}
            <div className="flex flex-col gap-1">
              <label className="font-caption text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Status</label>
              <div className="relative">
                <Icon name="FunnelIcon" size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select value={stageFilter} onChange={e => setStageFilter(e.target.value as StageKey | 'all')} className="pl-7 pr-7 py-1.5 rounded-lg border border-gray-200 font-body text-xs focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white appearance-none cursor-pointer" suppressHydrationWarning>
                  <option value="all">All Stages</option>
                  {PIPELINE_COLUMNS.map(col => <option key={col.id} value={col.id}>{col.title}</option>)}
                </select>
              </div>
            </div>

            {/* Contract Value */}
            <div className="flex flex-col gap-1">
              <label className="font-caption text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Contract Value</label>
              <div className="relative">
                <Icon name="CurrencyDollarIcon" size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select value={contractRange} onChange={e => setContractRange(Number(e.target.value))} className="pl-7 pr-7 py-1.5 rounded-lg border border-gray-200 font-body text-xs focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white appearance-none cursor-pointer" suppressHydrationWarning>
                  {CONTRACT_VALUE_OPTIONS.map((opt, i) => <option key={i} value={i}>{opt.label}</option>)}
                </select>
              </div>
            </div>

            {/* Location */}
            <div className="flex flex-col gap-1">
              <label className="font-caption text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Location</label>
              <div className="relative">
                <Icon name="MapPinIcon" size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input type="text" value={locationFilter} onChange={e => setLocationFilter(e.target.value)} placeholder="City or state…" className="pl-7 pr-3 py-1.5 rounded-lg border border-gray-200 font-body text-xs focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white w-36" suppressHydrationWarning />
              </div>
            </div>

            {/* Last Activity */}
            <div className="flex flex-col gap-1">
              <label className="font-caption text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Last Activity</label>
              <div className="relative">
                <Icon name="ClockIcon" size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select value={lastActivityIdx} onChange={e => setLastActivityIdx(Number(e.target.value))} className="pl-7 pr-7 py-1.5 rounded-lg border border-gray-200 font-body text-xs focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white appearance-none cursor-pointer" suppressHydrationWarning>
                  {LAST_ACTIVITY_OPTIONS.map((opt, i) => <option key={i} value={i}>{opt.label}</option>)}
                </select>
              </div>
            </div>

            {/* Tag filter */}
            <div className="flex flex-col gap-1">
              <label className="font-caption text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Tag</label>
              <div className="relative">
                <Icon name="TagIcon" size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select value={tagFilter ?? ''} onChange={e => setTagFilter(e.target.value || null)} className="pl-7 pr-7 py-1.5 rounded-lg border border-gray-200 font-body text-xs focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white appearance-none cursor-pointer" suppressHydrationWarning>
                  <option value="">All Tags</option>
                  {availableTags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2 pb-0.5">
              {hasActiveFilters && (
                <button onClick={clearAll} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-100 text-red-500 text-xs font-medium hover:bg-red-50 transition-colors">
                  <Icon name="XMarkIcon" size={12} /> Clear All
                </button>
              )}
              {hasActiveFilters && !aiQuery && (
                <button onClick={() => setShowSaveFilterModal(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-purple-200 text-purple-600 text-xs font-medium hover:bg-purple-50 transition-colors">
                  <Icon name="BookmarkIcon" size={12} /> Save Filter
                </button>
              )}
            </div>
          </div>
        )}

        {/* Quick filter chips */}
        {!showFilters && hasActiveFilters && !aiQuery && (
          <div className="flex items-center gap-2 flex-wrap">
            {stageFilter !== 'all' && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">{getStageMeta(stageFilter as StageKey).title}<button onClick={() => setStageFilter('all')} className="ml-0.5 hover:text-blue-900"><Icon name="XMarkIcon" size={10} /></button></span>}
            {contractRange !== 0 && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100">{CONTRACT_VALUE_OPTIONS[contractRange].label}<button onClick={() => setContractRange(0)} className="ml-0.5 hover:text-green-900"><Icon name="XMarkIcon" size={10} /></button></span>}
            {locationFilter && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">📍 {locationFilter}<button onClick={() => setLocationFilter('')} className="ml-0.5 hover:text-purple-900"><Icon name="XMarkIcon" size={10} /></button></span>}
            {lastActivityIdx !== 0 && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-100">🕐 {LAST_ACTIVITY_OPTIONS[lastActivityIdx].label}<button onClick={() => setLastActivityIdx(0)} className="ml-0.5 hover:text-orange-900"><Icon name="XMarkIcon" size={10} /></button></span>}
            {tagFilter && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-100">🏷 {availableTags.find(t => t.id === tagFilter)?.name}<button onClick={() => setTagFilter(null)} className="ml-0.5 hover:text-yellow-900"><Icon name="XMarkIcon" size={10} /></button></span>}
            <button onClick={clearAll} className="text-xs text-gray-400 hover:text-gray-600 underline">Clear all</button>
          </div>
        )}

        {/* Bulk Action Bar */}
        {someSelected && (
          <div className="flex items-center gap-2 mt-3 px-4 py-2.5 rounded-xl bg-blue-50 border border-blue-100">
            <span className="font-body text-sm font-semibold text-blue-700">{selectedIds.size} contact{selectedIds.size !== 1 ? 's' : ''} selected</span>
            <div className="flex-1" />
            <button
              onClick={() => setShowBulkTagModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 transition-colors"
            >
              <Icon name="TagIcon" size={13} />
              Bulk Tag
            </button>
            {/* Campaign Workflow (Mailchimp) removed in migration 20260529120000. */}
            <button onClick={() => setSelectedIds(new Set())} className="text-blue-400 hover:text-blue-600 ml-1">
              <Icon name="XMarkIcon" size={15} />
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <span className="font-body text-sm text-gray-500">Loading contacts…</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Icon name="ExclamationTriangleIcon" size={40} className="text-red-400 mx-auto mb-3" />
              <p className="font-body text-sm text-red-600">{error}</p>
              <button onClick={loadContacts} className="mt-3 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: '#033494' }}>Retry</button>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Icon name="UserGroupIcon" size={40} className="text-gray-300 mx-auto mb-3" />
              <p className="font-body text-sm text-gray-500">{hasActiveFilters ? 'No contacts match your filters.' : 'No contacts found.'}</p>
              {hasActiveFilters && <button onClick={clearAll} className="mt-3 font-body text-sm text-blue-600 hover:underline">Clear filters</button>}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    suppressHydrationWarning
                  />
                </th>
                <th className="text-left px-4 py-3 font-caption text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="text-left px-4 py-3 font-caption text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                <th className="text-left px-4 py-3 font-caption text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="text-left px-4 py-3 font-caption text-xs font-semibold text-gray-500 uppercase tracking-wider">Location</th>
                <th className="text-left px-4 py-3 font-caption text-xs font-semibold text-gray-500 uppercase tracking-wider">Stage</th>
                <th className="text-left px-4 py-3 font-caption text-xs font-semibold text-gray-500 uppercase tracking-wider">Contract Value</th>
                <th className="text-left px-4 py-3 font-caption text-xs font-semibold text-gray-500 uppercase tracking-wider">Tags</th>
                <th className="text-left px-4 py-3 font-caption text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Activity</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filtered.map(contact => {
                const stageMeta = getStageMeta(contact.stage);
                const initials = `${contact.customerFirstName.charAt(0)}${contact.customerLastName.charAt(0)}`.toUpperCase();
                const daysSince = getDaysSince(contact.columnEnteredAt, now);
                const tags = contactTagMap[contact.id] ?? [];
                const isSelected = selectedIds.has(contact.id);
                return (
                  <tr
                    key={contact.id}
                    className={`hover:bg-blue-50 cursor-pointer transition-colors group ${isSelected ? 'bg-blue-50' : ''}`}
                  >
                    <td className="px-4 py-3 w-10" onClick={e => { e.stopPropagation(); toggleSelect(contact.id); }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(contact.id)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        suppressHydrationWarning
                      />
                    </td>
                    <td className="px-4 py-3" onClick={() => setSelectedContact(contact)}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: `linear-gradient(135deg, ${stageMeta.color} 0%, #000150 100%)` }}>
                          {initials}
                        </div>
                        <div>
                          <p className="font-body text-sm font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">{contact.customerFirstName} {contact.customerLastName}</p>
                          <p className="font-caption text-xs text-gray-400">{contact.address}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3" onClick={() => setSelectedContact(contact)}>
                      <span className="font-body text-sm text-gray-600">{contact.email || '—'}</span>
                    </td>
                    <td className="px-4 py-3" onClick={() => setSelectedContact(contact)}>
                      <span className="font-body text-sm text-gray-600">{contact.phone || '—'}</span>
                    </td>
                    <td className="px-4 py-3" onClick={() => setSelectedContact(contact)}>
                      <span className="font-body text-sm text-gray-600">{contact.city}, {contact.state}</span>
                    </td>
                    <td className="px-4 py-3" onClick={() => setSelectedContact(contact)}>
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{ background: `${stageMeta.color}18`, color: stageMeta.color }}>
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: stageMeta.color }} />
                        {stageMeta.title}
                      </span>
                    </td>
                    <td className="px-4 py-3" onClick={() => setSelectedContact(contact)}>
                      <span className="font-body text-sm font-semibold text-gray-800">{formatCurrency(contact.contractValue)}</span>
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1 flex-wrap">
                        {tags.slice(0, 2).map(tag => (
                          <span key={tag.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${tag.color}18`, color: tag.color }}>
                            {tag.name}
                          </span>
                        ))}
                        {tags.length > 2 && <span className="text-[10px] text-gray-400">+{tags.length - 2}</span>}
                        {tags.length === 0 && <span className="text-[10px] text-gray-300">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3" onClick={() => setSelectedContact(contact)}>
                      {daysSince !== null ? (
                        <span className={`font-caption text-xs px-2 py-0.5 rounded-full font-medium ${daysSince <= 1 ? 'bg-green-50 text-green-700' : daysSince <= 7 ? 'bg-blue-50 text-blue-700' : daysSince <= 30 ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-600'}`}>
                          {daysSince === 0 ? 'Today' : daysSince === 1 ? 'Yesterday' : `${daysSince}d ago`}
                        </span>
                      ) : <span className="font-caption text-xs text-gray-400">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
